
/* global faceapi */

import { computed, ref } from "vue";
import { ScheduleViewModel } from "../OfficerSchedule/ScheduleViewModel";
declare const faceapi: any;

export default {
  name: "job-calls",
  props: ["baseUrl"],
  setup(props: any) {
    const StartingJob = ref<ScheduleViewModel>() as any;
    const EndingJob = ref<ScheduleViewModel>() as any;
    const RunningJob = ref(null) as any;
    var Guardtoken = localStorage.getItem("Guardtoken")!;
    var OfficerId = localStorage.getItem("OfficerId")!;

    // Face verification variables
    const showFaceVerification = ref(false);
    const verificationStatus = ref("Please look at camera and click Verify");
    const verificationVideoRef = ref<HTMLVideoElement | null>(null);
    const verificationCanvasRef = ref<HTMLCanvasElement | null>(null);
    const verificationStream = ref<MediaStream | null>(null);
    const pendingJobAction = ref<any>(null);
    const faceapiLoaded = ref(false);

    const showResultBanner = ref(false);
    const resultMessage = ref('');
    const resultBannerClass = ref('');

    const baseUrlAddress = computed(() => {
      return `${props.baseUrl}`;
    });

    // Load face-api models
    const loadFaceApiModels = async () => {
      try {
        if (typeof faceapi === 'undefined') {
          console.error('face-api.js not loaded');
          return false;
        }

        const MODEL_URL = '/models';
        await faceapi.nets.ssdMobilenetv1.loadFromUri(MODEL_URL);
        await faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL);
        await faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL);
        
        faceapiLoaded.value = true;
        console.log('Face-api models loaded');
        return true;
      } catch (error) {
        console.error('Failed to load face-api models:', error);
        return false;
      }
    };

    // Start webcam
    const startVerificationWebcam = async () => {
      try {
        if (verificationStream.value) {
          verificationStream.value.getTracks().forEach(track => track.stop());
        }
        
        verificationStream.value = await navigator.mediaDevices.getUserMedia({ 
          video: { width: { ideal: 640 }, height: { ideal: 480 } } 
        });
        
        if (verificationVideoRef.value) {
          verificationVideoRef.value.srcObject = verificationStream.value;
          await verificationVideoRef.value.play();
        }
      } catch (error) {
        console.error('Webcam error:', error);
        verificationStatus.value = 'Could not access webcam';
      }
    };

    // Extract face features
    const extractFaceFeatures = async (): Promise<number[] | null> => {
      const video = verificationVideoRef.value;
      if (!video || !video.videoWidth) {
        verificationStatus.value = 'Camera not ready';
        return null;
      }

      try {
        const detection = await faceapi
          .detectSingleFace(video)
          .withFaceLandmarks()
          .withFaceDescriptor();

        if (!detection) {
          verificationStatus.value = 'No face detected';
          return null;
        }

        // Draw detection on canvas
        const canvas = verificationCanvasRef.value;
        if (canvas) {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          const displaySize = { width: video.videoWidth, height: video.videoHeight };
          faceapi.matchDimensions(canvas, displaySize);
          const resizedDetection = faceapi.resizeResults(detection, displaySize);
          faceapi.draw.drawDetections(canvas, [resizedDetection]);
        }

        return Array.from(detection.descriptor);
      } catch (error) {
        console.error('Face extraction error:', error);
        return null;
      }
    };

    // Verify face with backend
    const verifyFaceWithBackend = async (features: number[]): Promise<boolean> => {
      try {
        const response = await fetch(`${baseUrlAddress.value}SecurityOfficers/VerifyFace`, {
          method: "POST",
          headers: {
            "Authorization": `Bearer ${Guardtoken}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify({ OfficerId: OfficerId, FacialFeatures: features })
        });

        const result = await response.json();
        console.log("Verification result:", result);
        return result.isMatch === true;
      } catch (error) {
        console.error('Verification error:', error);
        return false;
      }
    };

  
  // Update confirmFaceVerification
    const confirmFaceVerification = async () => {
      verificationStatus.value = 'Extracting face features...';
      const features = await extractFaceFeatures();
      
      if (!features) {
        verificationStatus.value = 'No face detected. Please try again.';
        return;
      }
      
      verificationStatus.value = 'Verifying face...';
      const isVerified = await verifyFaceWithBackend(features);
      
      if (isVerified) {
        verificationStatus.value = 'Face verified! Executing duty...';
        
        // Show success banner
        showResultBanner.value = true;
        resultMessage.value = '✅ Face Verification Passed! Duty will proceed.';
        resultBannerClass.value = 'success-banner';
        
        // Stop webcam
        if (verificationStream.value) {
          verificationStream.value.getTracks().forEach(track => track.stop());
          verificationStream.value = null;
        }
        
        // Close modal
        showFaceVerification.value = false;
        
        // Execute the original duty command
        if (pendingJobAction.value) {
          const { jobId, jobStatus, callTypeId, callType, remarks } = pendingJobAction.value;
          await DutyCommand(jobId, jobStatus, callTypeId, callType, remarks);
          pendingJobAction.value = null;
        }
        
        // Hide banner after 3 seconds
        setTimeout(() => {
          showResultBanner.value = false;
        }, 5000);
        
      } else {
        verificationStatus.value = 'Face verification failed! Access denied.';
        
        // Show error banner
        showResultBanner.value = true;
        //resultMessage.value = '❌ Face Verification Failed! Access Denied. Please try again.';
        resultMessage.value = '❌ Face Verification Failed! Please try again.';
        resultBannerClass.value = 'error-banner';
        
        // Close modal after delay
        setTimeout(() => {
          cancelFaceVerification();
          // Hide banner after modal closes
          setTimeout(() => {
            showResultBanner.value = false;
          }, 5000);
        }, 4000);
      }
    };


    // Cancel face verification
    const cancelFaceVerification = () => {
      if (verificationStream.value) {
        verificationStream.value.getTracks().forEach(track => track.stop());
        verificationStream.value = null;
      }
      showFaceVerification.value = false;
      pendingJobAction.value = null;
      verificationStatus.value = 'Please look at camera and click Verify';
    };

    // Check face before starting duty (NEW - replaces direct DutyCommand calls)
    const checkFaceAndStartDuty = async (jobId: number, jobStatus: number, callTypeId: number, callType: string, remarks: string) => {
      // Store the action
      pendingJobAction.value = { jobId, jobStatus, callTypeId, callType, remarks };
      
      // Load models if not loaded
      if (!faceapiLoaded.value) {
        verificationStatus.value = 'Loading face detection models...';
        await loadFaceApiModels();
      }
      
      // Show verification modal
      showFaceVerification.value = true;
      verificationStatus.value = 'Please look at camera and click Verify';
      
      // Start webcam
      await startVerificationWebcam();
    };

    // ============ YOUR ORIGINAL FUNCTIONS (EXACTLY AS THEY WERE) ============
    
    const GetCurrentJobsForOfficer = function () {
      StartingJob.value = null;
      EndingJob.value = null;
      RunningJob.value = null;
      GetStartingJobForOfficer("StartingJobForOfficer");
      GetStartingJobForOfficer("EndingJobForOfficer");
      GetStartingJobForOfficer("CheckcallStatusForOfficer");
    };

    const GetStartingJobForOfficer = function (endpoint: string) {
      let requestUrl = baseUrlAddress.value + "Schedule/" + endpoint;
      let requestOptions = {
        method: "POST",
        headers: {
          Authorization: `Bearer ${Guardtoken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          Id: parseInt(OfficerId),
        }),
      };
      fetch(requestUrl, requestOptions).then((response) => {
        response
          .json()
          .then((job) => {
            console.log("job.responseResult.response ", job.responseResult.response);
            return job.responseResult.response;
          })
          .then((j) => {
            if (j == null) return;
            switch (endpoint) {
              case "StartingJobForOfficer":
                StartingJob.value = j.scheduleViewModel;
                console.log("StartingJob.value ", StartingJob.value);
                break;
              case "EndingJobForOfficer":
                EndingJob.value = j.scheduleViewModel;
                console.log("EndingJob.value ", EndingJob.value);
                break;
              case "CheckcallStatusForOfficer":
                console.log("RunningJob", j);
                if (j.isCheckCallRequired)
                  RunningJob.value = {
                    jobId: j.jobId,
                    isCheckCallRequired: j.isCheckCallRequired,
                  };
                console.log("RunniningJob.value ", RunningJob.value);
                break;
            }
          });
      });
    };

    const getLocation = () => {
      return new Promise((resolve, reject) => {
        if (!("geolocation" in navigator)) {
          reject(new Error("Geolocation is not available."));
          return;
        }
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            const MAPBOX_TOKEN = "pk.eyJ1Ijoic2hvdWtldGFsaSIsImEiOiJja3Vicmxqb3UwYzlnMnFvNmNhMDJ5dmJiIn0.bsT85MRV63Qr_kyl2RpJbQ";
            const requestUrl = `https://api.mapbox.com/geocoding/v5/mapbox.places/${pos.coords.longitude},${pos.coords.latitude}.json?access_token=${MAPBOX_TOKEN}`;
            fetch(requestUrl, { method: "GET" })
              .then((response) => response.json())
              .then((result) => {
                const address = result?.features?.length > 0 ? result.features[0].place_name : null;
                resolve({ address, pos, accuracy: pos.coords.accuracy });
              })
              .catch((err) => reject(err));
          },
          (err) => reject(err),
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
        );
      });
    };

    const InsertCheckCall = function (jobId: number, callTypeId: number, callType: string, remarks: string) {
      console.log("InsertCheckCall called for jobId = " + jobId);
      let longitude = 0;
      let latitude = 0;
      let altitude = 0;
      let address = "";
      getLocation()
        .then((res: any) => {
          longitude = res.pos.coords.longitude;
          latitude = res.pos.coords.latitude;
          address = res.address;
          let requestUrl = baseUrlAddress.value + "JobCalls/AddJobCall";
          console.log("requestUrl for insert jobcall = " + requestUrl);
          let requestOptions = {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              Id: 0,
              JobId: jobId,
              Location: longitude + ", " + latitude + ", " + altitude,
              CallTypeId: callTypeId,
              CallType: callType,
              Remarks: address,
              CallTime: new Date(),
            }),
          };
          fetch(requestUrl, requestOptions)
            .then((response) => {
              response.json();
              GetCurrentJobsForOfficer();
            })
            .then((res) => {
              console.log("Job call inserted successfully");
            });
        })
        .catch((error) => {
          console.log("error occured while addingJobCall", error);
        });
    };

    const DutyCommand = async function (jobId: number, jobStatus: number, callTypeId: number, callType: string, remarks: string) {
      console.log("DutyCommand called for jobId =", jobId);
      console.log("callTypeId =", callTypeId);

      if (callTypeId == 2) {
        InsertCheckCall(jobId, callTypeId, callType, remarks);
      } else {
        let requestUrl = baseUrlAddress.value + "Schedule/UpdateJobStatusNew";
        let requestOptions = {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.Guardtoken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            Id: jobId,
            JobStatusId: jobStatus,
          }),
        };
        fetch(requestUrl, requestOptions)
          .then((response) => response.json())
          .then((res) => {
            console.log("status updated successfully......");
            InsertCheckCall(jobId, callTypeId, callType, remarks);
          });
      }
      GetCurrentJobsForOfficer();
    };

    GetCurrentJobsForOfficer();

    return {
      GetStartingJobForOfficer,
      GetCurrentJobsForOfficer,
      baseUrlAddress,
      DutyCommand,
      InsertCheckCall,
      StartingJob,
      EndingJob,
      RunningJob,
      // Face verification
      showFaceVerification,
      verificationStatus,
      verificationVideoRef,
      verificationCanvasRef,
      confirmFaceVerification,
      cancelFaceVerification,
      showResultBanner,
  resultMessage,
  resultBannerClass,
      checkFaceAndStartDuty,
    };
  },
};
